import { useEffect, useState } from "react";
import { differenceInCalendarDays, format } from "date-fns";
import { api } from "../../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, CalendarClock, CalendarOff } from "lucide-react";
import { ScheduleSection } from "@/components/schedule/ScheduleSection";

const endLine = (project) => {
  if (project.status === "completed") return "Project completed";
  if (!project.end_date) return "No end date · ongoing";
  const left = differenceInCalendarDays(new Date(project.end_date), new Date());
  const d = format(new Date(project.end_date), "MMM dd");
  return left < 0 ? `Ended ${d} · ${-left}d past end` : `Ends ${d} · ${left}d left`;
};

const Count = ({ n, label, tone }) => (
  <span className="flex items-baseline gap-1">
    <span className={`text-2xl font-bold ${n ? tone : "text-gray-300"}`}>{n}</span>
    <span className="text-[11px] text-gray-500">{label}</span>
  </span>
);

// Submittal schedule health for one project — same buckets as the dashboard Schedule Overview
export const ProjectScheduleKpi = ({ project }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get(`/dashboard/schedule-overview?project_id=${project.id}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [project.id]);
  const c = data?.counts || { overdue: 0, today_tomorrow: 0, unplanned: 0 };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="text-left px-6 py-5 min-w-0 hover:bg-gray-50 transition-colors rounded-r-xl" data-testid="kpi-schedule">
        <p className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">Schedule</p>
        <div className="flex items-center gap-4 mt-1" data-testid="kpi-schedule-counts">
          <Count n={c.overdue} label="overdue" tone="text-rose-600" />
          <Count n={c.today_tomorrow} label="today/tmrw" tone="text-amber-600" />
          <Count n={c.unplanned} label="unplanned" tone="text-slate-600" />
        </div>
        <p className="text-xs text-gray-500 mt-1">{endLine(project)}</p>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl bg-white max-h-[85vh] overflow-y-auto" data-testid="project-schedule-dialog">
          <DialogHeader><DialogTitle>Schedule — {project.project_number} {project.name}</DialogTitle></DialogHeader>
          {!["yet_to_start", "in_progress"].includes(project.status) && (
            <p className="text-sm text-gray-500" data-testid="project-schedule-inactive">Schedule tracking covers only Yet to Start and In Progress projects.</p>
          )}
          <div className="space-y-4">
            <ScheduleSection testid="psched-overdue" Icon={AlertTriangle} title="Overdue" subtitle="Planned date has passed, action not yet taken"
              accentBg="bg-rose-50 border-rose-200" accentIcon="text-rose-600 bg-rose-100" countBadge="bg-rose-600 text-white"
              rows={data?.overdue || []} emptyText="No overdue schedules." showOverdueDays hideProject defaultOpen={c.overdue > 0} />
            <ScheduleSection testid="psched-today-tomorrow" Icon={CalendarClock} title="Planned for Today / Tomorrow" subtitle={`${data?.today || ""} · ${data?.tomorrow || ""}`}
              accentBg="bg-amber-50 border-amber-200" accentIcon="text-amber-700 bg-amber-100" countBadge="bg-amber-600 text-white"
              rows={data?.today_tomorrow || []} emptyText="Nothing scheduled for today or tomorrow." hideProject defaultOpen={c.today_tomorrow > 0} />
            <ScheduleSection testid="psched-unplanned" Icon={CalendarOff} title="Unplanned" subtitle="Active step has no planned date"
              accentBg="bg-slate-50 border-slate-200" accentIcon="text-slate-600 bg-slate-200" countBadge="bg-slate-600 text-white"
              rows={data?.unplanned || []} emptyText="All active steps have a planned date." hideProject />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
